import { WidgetProps } from '../../models/widget.model';
import DataGrid from '../datagrid/DataGrid';

import CardsGrid, { GridCardType } from '../cards-grid/CardsGrid';
import { useEffect, useMemo } from 'react';

import { process, State } from '@progress/kendo-data-query';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { selectViewStateForWidget } from '../../store/slices/view-toggle.slice';
import { TagRepositoryRegisterPayload } from '../../models/tag.model';
import { addTagRepository, selectTagEditorVisible } from '../../store/slices/tag-editor.slice';
import { ListResponse } from '../../store/api/chart.api';
import { TagCell } from '../datagrid/TagCell';
import SentimentCell from 'common/components/datagrid/Sentiment';
import { formatAppDate } from '../../hooks/utils';
import { TagEditor } from '../tags/TagEditor';
import useFeatureFlags from 'common/hooks/useFeatureFlags';
import { Feature } from 'common/models/page-config.models';
import { AttachmentColumn } from 'common/components/widgets/widget-subcomponents/Attachment';
import { DateCell } from '../datagrid/DateCell';
import TeamMemberCell from '../datagrid/TeamMemberCell';
import ProfileNameCell from '../datagrid/ProfileNameCell';
import TextCell from '../datagrid/TextCell';
import { ColumnModel } from 'common/models/widget-config.model';

export const columns: ColumnModel[] = [
  {
    title: 'Date',
    field: 'date',
    filter: 'date',
    sortable: true,
    width: '100px',
    groupable: true,
    cell: DateCell,
  },
  {
    title: 'Team member',
    field: 'fieldTeam',
    width: '150px',
    groupable: true,
    cell: TeamMemberCell,
  },
  {
    title: 'Profile',
    field: 'profile',
    width: '150px',
    groupable: true,
    sortable: true,
    cell: ProfileNameCell,
  },
  {
    title: 'Specialty',
    field: 'specialtyName',
    width: '120px',
    groupable: true,
  },
  {
    title: 'Category',
    field: 'survey',
    width: '150px',
    groupable: true,
  },
  {
    title: 'Type',
    field: 'responseType',
    width: '150px',
    groupable: true,
  },
  {
    groupable: true,
    title: 'Question',
    field: 'question',
    width: '220px',
  },
  {
    title: 'Sentiment',
    field: 'sentimentLabel',
    width: '150px',
    groupable: false,
    cell: SentimentCell,
  },
  {
    title: 'Sentiment Reason',
    field: 'sentimentReason',
    width: '250px',
  },
  {
    title: 'Response',
    field: 'response',
    groupable: true,
    width: '150px',
    cell: TextCell,
  },
  {
    title: 'Response Details',
    field: 'responseDetail',
    width: '400px',
    groupable: false,
    cell: TextCell,
  },
  {
    title: 'Tags',
    field: 'tags',
    width: '400px',
    groupable: false,
    sortable: false,
    cell: TagCell,
  },
  {
    sortable: true,
    title: 'Insight Source',
    field: 'insightSourceName',
    width: '220px',
    groupable: false,
    cell: TextCell,
  },
];

const card: GridCardType = {
  topRowParams: [
    {
      name: 'date',
      sequence: 1,
      callback: (date: any) => formatAppDate(new Date(date), 'MM/dd/yyyy'),
    },
    {
      name: 'profile',
      sequence: 2,
    },
    {
      name: 'specialtyName',
      sequence: 3,
    },
    {
      name: 'fieldTeam',
      sequence: 4,
    },
  ],
  descriptionTitle: 'response',
  descriptionBody: 'responseDetail',
  tags: 'tags',
};

const VIEW = {
  GRID: 0,
  CARD_LIST: 1,
};

const TAG_REPOSITORY = 'response';

export default function WidgetResponseDetailsExtended(props: Readonly<WidgetProps<ListResponse>>) {
  const dispatch = useAppDispatch();
  const widgetViews = useAppSelector(selectViewStateForWidget);
  const viewIndex = widgetViews[props.id] ?? 0;
  const dataState = useMemo(() => props.data as State, [props.data]);
  const total = useMemo<number>(() => props.data?.total, [props.data.total]);
  const dataResult = useMemo(
    () => process(props.data.data, dataState),
    [props.data.data, dataState]
  );
  const { hasFeature } = useFeatureFlags();
  const hasFileUpload = hasFeature([Feature.CaptureFileUpload, Feature.CaptureOrganicFileUpload]);
  const hasSentiments = hasFeature(Feature.Sentiments);

  const updatedColumns = useMemo(() => {
    const newColumns = [...columns];
    if (hasFileUpload) {
      newColumns.push(AttachmentColumn);
    }
    if (hasSentiments) {
      const sentimentLabelIndex = newColumns.findIndex((col) => col.field === 'sentimentLabel');
      newColumns[sentimentLabelIndex] = {
        title: 'Sentiment',
        field: 'sentimentLabel',
        width: '150px',
        groupable: false,
        cell: SentimentCell,
      };
      return newColumns.filter((col) => col.field !== 'sentimentReason');
    } else
      return newColumns.filter(
        (col) => col.field !== 'sentimentLabel' && col.field !== 'sentimentReason'
      );
  }, [hasFileUpload, hasSentiments]);

  const tagEditorVisible = useAppSelector(selectTagEditorVisible);
  const tagRepository = useMemo(() => {
    const payload: TagRepositoryRegisterPayload = {
      repository: TAG_REPOSITORY,
      records: {},
    };
    dataResult.data.forEach((row: any) => {
      payload.records[row.responseId] = row.tags;
    });
    return payload;
  }, [dataResult]);

  useEffect(() => {
    dispatch(addTagRepository(tagRepository));
  }, [tagRepository, dispatch]);

  if (viewIndex === VIEW.GRID) {
    return (
      <>
        {tagEditorVisible && <TagEditor />}
        <DataGrid
          widgetId={props.id}
          className="treelist-grid-category-response"
          {...dataState}
          groupable={props.groupable}
          total={total}
          sortable={true}
          data={dataResult}
          columns={updatedColumns}
          resizable={true}
        />
      </>
    );
  }
  return (
    <div className="widget-response-details">
      {tagEditorVisible && <TagEditor />}
      <div className="k-scrollable">
        <CardsGrid total={props.data.total} widgetId={props.id} data={dataResult} card={card} />
      </div>
    </div>
  );
}
